// 📄 pages/Proyectos/components/FacturaCard.tsx
// Tarjeta resumen de una factura en los resultados de "Buscar Facturas"
// (ver BuscarFacturas.tsx) — mismo criterio que ProveedorCard.tsx (SLP):
// los nombres de campo son tal cual los documenta la API de Ariba
// (Invoice Header Data Extraction), confirmados contra una respuesta real
// antes de codear.
import { Receipt, ChevronRight } from 'lucide-react';
import { Factura } from '../../../services/comercioFacturasService.ts';

interface FacturaCardProps {
  factura: Factura;
  onVerDetalle: () => void;
}

// Paleta por estado — cubre los valores documentados de documentStatus.
const colorEstado = (valor?: string): string => {
  const v = (valor || '').toLowerCase();
  if (['paid', 'approved', 'sent', 'acknowledged'].includes(v)) return 'text-emerald-300 bg-emerald-500/10 border-emerald-500/30';
  if (['pending approval', 'in progress', 'on hold', 'queued'].includes(v)) return 'text-amber-300 bg-amber-500/10 border-amber-500/30';
  if (['canceled', 'failed', 'rejected', 'obsoleted'].includes(v)) return 'text-red-300 bg-red-500/10 border-red-500/30';
  return 'text-gray-400 bg-white/5 border-white/20';
};

const formatearMonto = (monto?: { amount?: number; currencyCode?: string }): string | null => {
  if (!monto || typeof monto.amount !== 'number') return null;
  return `${monto.amount.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${monto.currencyCode || ''}`.trim();
};

const formatearFecha = (valor?: string): string | null => {
  if (!valor) return null;
  const fecha = new Date(valor);
  if (Number.isNaN(fecha.getTime())) return null;
  return fecha.toLocaleDateString('es-ES', { year: 'numeric', month: 'short', day: 'numeric' });
};

export default function FacturaCard({ factura, onVerDetalle }: FacturaCardProps) {
  const monto = formatearMonto(factura.totalAmount);
  const fecha = formatearFecha(factura.documentDate);

  return (
    <button
      type="button"
      onClick={onVerDetalle}
      className="text-left w-full bg-white/5 hover:bg-white/[0.07] border border-white/10 hover:border-indigo-500/40 rounded-2xl p-4 space-y-3 transition-all group"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-start gap-2.5 min-w-0">
          <div className="w-9 h-9 rounded-xl bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
            <Receipt className="w-4 h-4 text-indigo-300" />
          </div>
          <div className="min-w-0">
            <h4 className="text-sm font-semibold text-white leading-snug truncate">{factura.documentNumber || 'Sin número'}</h4>
            <p className="text-[11px] text-gray-500 truncate">{factura.supplierName || 'Proveedor sin nombre'}</p>
          </div>
        </div>
        <ChevronRight className="w-4 h-4 text-gray-600 group-hover:text-indigo-400 flex-shrink-0 mt-1 transition-colors" />
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        {factura.documentStatus && (
          <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${colorEstado(factura.documentStatus)}`}>
            {factura.documentStatus}
          </span>
        )}
        {factura.routingStatus && (
          <span className="text-[10px] font-medium px-2 py-0.5 rounded-full border text-gray-400 bg-white/5 border-white/10">
            {factura.routingStatus}
          </span>
        )}
      </div>

      {(monto || fecha) && (
        <div className="flex items-center justify-between pt-1 border-t border-white/5">
          {fecha && <p className="text-xs text-gray-400">{fecha}</p>}
          {monto && <p className="text-sm font-semibold text-white">{monto}</p>}
        </div>
      )}
    </button>
  );
}
